import { Suspense, lazy, type ReactNode } from "react";
import {
  Navigate,
  Outlet,
  createBrowserRouter,
  useLocation,
} from "react-router";
import { isSessionExpired, useMe } from "./lib/auth";
import { Spinner } from "./components/ui/misc";
import { Button } from "./components/ui/button";
import { RefreshCw } from "lucide-react";
import { LoginPage } from "./pages/auth/LoginPage";
import { ForgotPasswordPage } from "./pages/auth/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/auth/ResetPasswordPage";
import { AcceptInvitePage } from "./pages/auth/AcceptInvitePage";
import { AppShell } from "./pages/app/AppShell";
import type { ChannelKey } from "@theadam/shared";
import { NotFoundPage } from "./pages/NotFoundPage";
import { RouteError } from "./pages/RouteError";

// Grafik içeren sayfalar (ECharts) ayrı parçada yüklenir; ilk açılış hafifler.
// Sayfalar ihtiyaç duyulunca yüklenir (ilk açılış hızlı; T-09).
const OnboardingPage = lazy(() => import("./pages/onboarding/OnboardingPage").then((m) => ({ default: m.OnboardingPage })));
const IntegrationsPage = lazy(() => import("./pages/app/IntegrationsPage").then((m) => ({ default: m.IntegrationsPage })));
const IntegrationAppPage = lazy(() => import("./pages/app/IntegrationAppPage").then((m) => ({ default: m.IntegrationAppPage })));
const ChangePasswordPage = lazy(() => import("./pages/app/ChangePasswordPage").then((m) => ({ default: m.ChangePasswordPage })));
const SettingsPage = lazy(() => import("./pages/app/SettingsPage").then((m) => ({ default: m.SettingsPage })));
const OrdersPage = lazy(() =>
  import("./pages/app/OrdersPage").then((m) => ({
    default: ({ kind }: { kind?: "kargo" | "iadeler" }) => <m.OrdersPage preset={kind === "kargo" ? m.SHIPPING_PRESET : kind === "iadeler" ? m.RETURNS_PRESET : undefined} />,
  })),
);
const ProductsPage = lazy(() => import("./pages/app/ProductsPage").then((m) => ({ default: ({ platform }: { platform: ChannelKey }) => <m.ProductsPage platform={platform} /> })));
const AllProductsPage = lazy(() => import("./pages/app/AllProductsPage").then((m) => ({ default: m.AllProductsPage })));
const ProductsRedirect = lazy(() => import("./pages/app/ProductsPage").then((m) => ({ default: m.ProductsRedirect })));
const MatchingPage = lazy(() => import("./pages/app/MatchingPage").then((m) => ({ default: m.MatchingPage })));
const CustomersPage = lazy(() => import("./pages/app/CustomersPage").then((m) => ({ default: m.CustomersPage })));
const QuestionsPage = lazy(() => import("./pages/app/QuestionsPage").then((m) => ({ default: m.QuestionsPage })));
const NotificationsPage = lazy(() => import("./pages/app/NotificationsPage").then((m) => ({ default: m.NotificationsPage })));
const OverviewPage = lazy(() =>
  import("./pages/app/OverviewPage").then((m) => ({ default: m.OverviewPage })),
);
const ProductCardPage = lazy(() =>
  import("./pages/app/ProductCardPage").then((m) => ({ default: m.ProductCardPage })),
);
const TrendyolSettingsPage = lazy(() =>
  import("./pages/app/TrendyolSettingsPage").then((m) => ({ default: m.TrendyolSettingsPage })),
);
const NewProductPage = lazy(() =>
  import("./pages/app/NewProductPage").then((m) => ({ default: m.NewProductPage })),
);
const CompletenessPage = lazy(() =>
  import("./pages/app/CompletenessPage").then((m) => ({ default: m.CompletenessPage })),
);
const ReleasesPage = lazy(() =>
  import("./pages/app/ReleasesPage").then((m) => ({ default: m.ReleasesPage })),
);
const IyzicoPage = lazy(() =>
  import("./pages/app/IyzicoPage").then((m) => ({ default: m.IyzicoPage })),
);
const ReportsPage = lazy(() =>
  import("./pages/app/ReportsPage").then((m) => ({ default: m.ReportsPage })),
);
const page = (node: ReactNode) => (
  <Suspense fallback={<Spinner />}>{node}</Suspense>
);

function RequireAuth() {
  const { data: me, isLoading, error, refetch, isFetching } = useMe();
  const location = useLocation();
  if (isLoading) return <Spinner label="Oturum kontrol ediliyor" />;
  if (error)
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 p-8 text-center text-sm">
        <p className="font-medium text-danger">Sunucuya ulaşılamadı.</p>
        <p className="max-w-md text-muted-foreground">Bağlantı geçici olarak takıldı. Panel 15 saniyede bir kendiliğinden yeniden dener; isterseniz hemen deneyin.</p>
        <Button onClick={() => void refetch()} loading={isFetching}>
          <RefreshCw /> Tekrar dene
        </Button>
      </div>
    );
  if (!me)
    return <Navigate to="/giris" replace state={{ from: `${location.pathname}${location.search}${location.hash}`, expired: isSessionExpired() }} />;
  const needsOnboarding =
    !me.onboarding.completed && me.permissions.includes("onboarding:manage");
  // Hesap sayfaları kurulum bitmeden de açılabilir (ör. ilk şifreyi değiştirmek).
  const allowedDuringOnboarding =
    location.pathname === "/kurulum" || location.pathname.startsWith("/hesap/");
  if (needsOnboarding && !allowedDuringOnboarding)
    return <Navigate to="/kurulum" replace />;
  // Çalışma alanında iki aşamalı doğrulama zorunluysa önce kurulur.
  const onSecurityPage =
    location.pathname === "/ayarlar" && new URLSearchParams(location.search).get("sekme") === "sifre";
  if (me.security?.twoFactorRequired && !me.security.twoFactorEnabled && !onSecurityPage && !allowedDuringOnboarding)
    return <Navigate to="/ayarlar?sekme=sifre" replace />;
  return <Outlet />;
}

function GuestOnly() {
  const { data: me, isLoading } = useMe();
  if (isLoading) return <Spinner />;
  if (me) return <Navigate to="/" replace />;
  return <Outlet />;
}

export const router = createBrowserRouter([
  {
    element: <GuestOnly />,
    errorElement: <RouteError />,
    children: [
      { path: "/giris", element: <LoginPage /> },
      { path: "/sifremi-unuttum", element: <ForgotPasswordPage /> },
    ],
  },
  {
    path: "/sifre-sifirla",
    element: <ResetPasswordPage />,
  },
  {
    path: "/davet",
    element: <AcceptInvitePage />,
    errorElement: <RouteError />,
  },
  {
    element: <RequireAuth />,
    errorElement: <RouteError />,
    children: [
      { path: "/kurulum", element: page(<OnboardingPage />) },
      {
        element: <AppShell />,
        children: [
          {
            // Hata menü ve üst çubuk yerinde kalarak sayfa alanında gösterilir.
            errorElement: <RouteError />,
            children: [
              { index: true, element: page(<OverviewPage />) },
              { path: "/ayarlar", element: page(<SettingsPage />) },
              {
                path: "/ayarlar/entegrasyonlar",
                element: page(<IntegrationsPage />),
              },
              { path: "/ayarlar/entegrasyonlar/:app", element: page(<IntegrationAppPage />) },
              { path: "/hesap/sifre", element: page(<ChangePasswordPage />) },
              { path: "/siparisler", element: page(<OrdersPage />) },
              { path: "/musteriler", element: page(<CustomersPage />) },
              { path: "/odemeler/iyzico", element: page(<IyzicoPage />) },
              { path: "/sistem/surumler", element: page(<ReleasesPage />) },
              { path: "/sorular", element: page(<QuestionsPage />) },
              {
                path: "/kargo",
                element: page(<OrdersPage key="kargo" kind="kargo" />),
              },
              {
                path: "/iadeler",
                element: page(<OrdersPage key="iadeler" kind="iadeler" />),
              },
              { path: "/urunler", element: page(<ProductsRedirect />) },
              { path: "/urunler/tumu", element: page(<AllProductsPage />) },
              {
                path: "/urunler/ideasoft",
                element: page(<ProductsPage key="ideasoft" platform="ideasoft" />),
              },
              {
                path: "/urunler/trendyol",
                element: page(<ProductsPage key="trendyol" platform="trendyol" />),
              },
              { path: "/urunler/hepsiburada", element: page(<ProductsPage key="hepsiburada" platform="hepsiburada" />) },
              { path: "/urunler/woocommerce", element: page(<ProductsPage key="woocommerce" platform="woocommerce" />) },
              { path: "/urunler/ticimax", element: page(<ProductsPage key="ticimax" platform="ticimax" />) },
              { path: "/urunler/eslestirme", element: page(<MatchingPage />) },
              { path: "/urunler/yeni", element: page(<NewProductPage />) },
              { path: "/urunler/trendyol-ayarlari", element: page(<TrendyolSettingsPage />) },
              { path: "/urunler/eksikler", element: page(<CompletenessPage />) },
              { path: "/urunler/:id", element: page(<ProductCardPage />) },
              { path: "/raporlar", element: page(<ReportsPage />) },
              { path: "/bildirimler", element: page(<NotificationsPage />) },
            ],
          },
        ],
      },
    ],
  },
  { path: "*", element: <NotFoundPage /> },
]);
